import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Paper
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { AxiosError } from 'axios';
import api from '../api';
import { apiEndpoints } from '../config';

type EntitlementType = 'Base' | 'Plan' | 'Metered';

interface FeatureCatalogItem {
  featureKey: string;
  entitlementType: EntitlementType;
  isActive: boolean;
  dateCreated: string;
  dateModified: string;
}

interface FeatureCatalogResponse {
  items: FeatureCatalogItem[];
  paging: {
    page: number;
    pageSize: number;
    totalCount: number;
  };
}

interface FeatureCatalogMutationResponse {
  item: FeatureCatalogItem;
  impactedMappings?: number;
}

interface ApiErrorResponse {
  code?: string;
  message?: string;
  error?: string;
  details?: {
    activeMappings?: number;
  };
}

const defaultCreateForm = {
  featureKey: '',
  entitlementType: 'Plan' as EntitlementType
};

const defaultEditForm = {
  entitlementType: 'Plan' as EntitlementType,
  isActive: true
};

const FeatureCatalog: React.FC = () => {
  const [items, setItems] = useState<FeatureCatalogItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FeatureCatalogItem | null>(null);
  const [createForm, setCreateForm] = useState(defaultCreateForm);
  const [editForm, setEditForm] = useState(defaultEditForm);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [featureToDeactivate, setFeatureToDeactivate] = useState<FeatureCatalogItem | null>(null);
  const [activeMappingsCount, setActiveMappingsCount] = useState(0);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');

  useEffect(() => {
    void loadFeatures();
  }, [page, rowsPerPage, search]);

  const loadFeatures = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get<FeatureCatalogResponse>(apiEndpoints.mappings.featureCatalog, {
        params: {
          page: page + 1,
          pageSize: rowsPerPage,
          search: search || undefined
        }
      });
      setItems(response.data.items ?? []);
      setTotalCount(response.data.paging?.totalCount ?? 0);
    } catch (err) {
      setError(getApiError(err, 'Failed to load Feature Catalog.'));
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (): Promise<void> => {
    if (!createForm.featureKey.trim()) {
      setError('Feature Key is required.');
      return;
    }

    try {
      setLoading(true);
      await api.post<FeatureCatalogMutationResponse>(apiEndpoints.mappings.featureCatalog, {
        featureKey: createForm.featureKey.trim().toUpperCase(),
        entitlementType: createForm.entitlementType,
        isActive: true
      });
      setMessage('Feature created.');
      setCreateOpen(false);
      setCreateForm(defaultCreateForm);
      await loadFeatures();
    } catch (err) {
      setError(getApiError(err, 'Failed to create feature.'));
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (item: FeatureCatalogItem): void => {
    setSelectedItem(item);
    setEditForm({ entitlementType: item.entitlementType, isActive: item.isActive });
    setEditOpen(true);
  };

  const handleEdit = async (): Promise<void> => {
    if (!selectedItem) {
      return;
    }

    try {
      setLoading(true);
      await api.put<FeatureCatalogMutationResponse>(
        apiEndpoints.mappings.featureCatalogByKey(selectedItem.featureKey),
        {
          entitlementType: editForm.entitlementType,
          isActive: editForm.isActive
        }
      );
      setMessage('Feature updated.');
      setEditOpen(false);
      setSelectedItem(null);
      await loadFeatures();
    } catch (err) {
      setError(getApiError(err, 'Failed to update feature.'));
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (item: FeatureCatalogItem): Promise<void> => {
    if (!item.isActive) {
      try {
        setLoading(true);
        await api.post(apiEndpoints.mappings.featureCatalogActivate(item.featureKey));
        setMessage('Feature activated.');
        await loadFeatures();
      } catch (err) {
        setError(getApiError(err, 'Failed to update feature state.'));
      } finally {
        setLoading(false);
      }
      return;
    }

    setFeatureToDeactivate(item);
    setDeactivateError('');
    await deactivateFeature(item, false);
  };

  const deactivateFeature = async (item: FeatureCatalogItem, force: boolean): Promise<void> => {
    try {
      setDeactivateLoading(true);
      const response = await api.post<FeatureCatalogMutationResponse>(
        apiEndpoints.mappings.featureCatalogDeactivate(item.featureKey),
        { force }
      );

      const impactedMappings = response.data.impactedMappings ?? 0;
      const impactText = force && impactedMappings > 0
        ? ` and ${impactedMappings} related mapping(s)`
        : '';

      setMessage(`Feature deactivated${impactText}.`);
      closeDeactivateDialog();
      await loadFeatures();
    } catch (err) {
      const apiError = err as AxiosError<ApiErrorResponse>;
      const status = apiError.response?.status;
      const code = apiError.response?.data?.code ?? apiError.response?.data?.error;

      if (status === 409 && code === 'FeatureHasActiveMappings' && !force) {
        setActiveMappingsCount(apiError.response?.data?.details?.activeMappings ?? 0);
        setDeactivateConfirmOpen(true);
      } else if (force) {
        setDeactivateError(getApiError(err, 'Failed to force deactivate feature.'));
      } else {
        setError(getApiError(err, 'Failed to update feature state.'));
      }
    } finally {
      setDeactivateLoading(false);
    }
  };

  const closeDeactivateDialog = (): void => {
    setDeactivateConfirmOpen(false);
    setFeatureToDeactivate(null);
    setActiveMappingsCount(0);
    setDeactivateError('');
  };

  const handleConfirmForceDeactivate = async (): Promise<void> => {
    if (!featureToDeactivate) {
      return;
    }

    await deactivateFeature(featureToDeactivate, true);
  };

  const handlePageChange = (_event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Feature Catalog
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage feature definitions used by entitlement provisioning.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Search Feature Key"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          size="small"
        />
        <Button variant="outlined" onClick={() => void loadFeatures()}>
          Refresh
        </Button>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Add Feature
        </Button>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Feature Key</TableCell>
              <TableCell>Entitlement Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date Modified</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.featureKey}>
                <TableCell>{item.featureKey}</TableCell>
                <TableCell>{item.entitlementType}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={item.isActive ? 'Active' : 'Inactive'}
                    color={item.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>{new Date(item.dateModified).toLocaleString()}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => openEdit(item)}>Edit</Button>
                    <Button size="small" onClick={() => void toggleActive(item)}>
                      {item.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Feature</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Feature Key"
              value={createForm.featureKey}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, featureKey: e.target.value }))}
              helperText="Uppercase letters, numbers, underscore, period."
            />
            <FormControl>
              <InputLabel>Entitlement Type</InputLabel>
              <Select
                label="Entitlement Type"
                value={createForm.entitlementType}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, entitlementType: e.target.value as EntitlementType }))}
              >
                <MenuItem value="Base">Base</MenuItem>
                <MenuItem value="Plan">Plan</MenuItem>
                <MenuItem value="Metered">Metered</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleCreate()}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Feature</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Feature Key" value={selectedItem?.featureKey ?? ''} disabled />
            <FormControl>
              <InputLabel>Entitlement Type</InputLabel>
              <Select
                label="Entitlement Type"
                value={editForm.entitlementType}
                onChange={(e) => setEditForm((prev) => ({ ...prev, entitlementType: e.target.value as EntitlementType }))}
              >
                <MenuItem value="Base">Base</MenuItem>
                <MenuItem value="Plan">Plan</MenuItem>
                <MenuItem value="Metered">Metered</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={editForm.isActive ? 'active' : 'inactive'}
                onChange={(e) => setEditForm((prev) => ({ ...prev, isActive: e.target.value === 'active' }))}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleEdit()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deactivateConfirmOpen}
        onClose={() => {
          if (!deactivateLoading) {
            closeDeactivateDialog();
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Deactivate Feature with Active Mappings
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {deactivateError && (
              <Alert severity="error" onClose={() => setDeactivateError('')}>
                {deactivateError}
              </Alert>
            )}
            <Alert severity="warning">
              This feature has {activeMappingsCount} active mapping(s).
            </Alert>
            <Typography variant="body2">
              Deactivating this feature will also deactivate all related mappings. This action cannot be undone.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Feature: {featureToDeactivate?.featureKey}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeactivateDialog} disabled={deactivateLoading}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleConfirmForceDeactivate()}
            color="warning"
            variant="contained"
            disabled={deactivateLoading}
          >
            {deactivateLoading && <CircularProgress size={18} sx={{ mr: 1 }} />}
            Deactivate & Mappings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function getApiError(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ message?: string; error?: string }>;
  const data = axiosError.response?.data;
  return data?.message ?? data?.error ?? fallback;
}

export default FeatureCatalog;
